/**
 * Typed API error + the central error-code → UX classification
 * (reference/docs/UI-RECOMMENDATIONS.md §4). Screens key off `classifyError`,
 * never off message strings, and override only where they add specific copy.
 */

export class ApiError extends Error {
  readonly status: number
  readonly errorCode: string
  readonly errors: string[]
  /** Retry-After header value in seconds, when the server sent one (429). */
  readonly retryAfterSeconds: number | undefined

  constructor(args: {
    status: number
    errorCode?: string | null | undefined
    message?: string | null | undefined
    errors?: string[] | null | undefined
    retryAfterSeconds?: number | undefined
  }) {
    super(args.message ?? 'Something went wrong. Please try again.')
    this.name = 'ApiError'
    this.status = args.status
    this.errorCode = args.errorCode ?? statusFallbackCode(args.status)
    this.errors = args.errors ?? []
    this.retryAfterSeconds = args.retryAfterSeconds
  }
}

function statusFallbackCode(status: number): string {
  switch (status) {
    case 0:
      return 'NETWORK_ERROR'
    case 401:
      return 'UNAUTHORIZED'
    case 403:
      return 'FORBIDDEN'
    case 404:
      return 'NOT_FOUND'
    case 409:
      return 'CONFLICT'
    case 412:
      return 'PRECONDITION_FAILED'
    case 422:
      return 'INVALID_STATE_TRANSITION'
    case 429:
      return 'RATE_LIMIT_EXCEEDED'
    default:
      return status >= 500 ? 'SERVER_ERROR' : 'UNKNOWN_ERROR'
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/**
 * UX classes (UI-RECOMMENDATIONS §4 classes A–H + default toast):
 * - field      → inline error under the offending input
 * - blocking   → dedicated full-screen state page
 * - reauth     → open the re-authentication modal, then replay the mutation
 * - conflict   → toast + auto-refetch the entity ("reload and retry")
 * - degraded   → degraded-service banner with retry affordance
 * - rate-limit → cooldown UI, honour Retry-After
 * - permission → hide or explain; never a raw error dump
 * - payment    → route into the payment remediation flow
 * - toast      → default toast with message + retry affordance
 */
export type ErrorClass =
  | 'field'
  | 'blocking'
  | 'reauth'
  | 'conflict'
  | 'degraded'
  | 'rate-limit'
  | 'permission'
  | 'payment'
  | 'toast'

const FIELD_CODES = new Set([
  'VALIDATION_ERROR',
  'VALIDATION_FAILED',
  'EMAIL_ALREADY_EXISTS',
  'ORGANIZATION_SLUG_TAKEN',
  'TENANT_SLUG_TAKEN',
  'INVITE_EMAIL_MISMATCH',
  'EMAIL_DOMAIN_NOT_ALLOWED',
  'CURRENCY_NOT_SUPPORTED',
  'COUNTRY_NOT_SUPPORTED',
  'AMBIGUOUS_PRODUCT_SLUG',
  'TENANT_DOMAIN_INVALID',
  'WEBHOOK_ENDPOINT_URL_INVALID',
])

const BLOCKING_CODES = new Set([
  'APPROVAL_PENDING',
  'EMAIL_NOT_VERIFIED',
  'USER_DEACTIVATED',
  'USER_ALREADY_ANONYMIZED',
  'TENANT_DUNNING_SUSPENDED',
  'TENANT_PROVISIONING_IN_PROGRESS',
  'QUOTA_EXCEEDED',
])

const REAUTH_CODES = new Set(['RECENT_AUTH_REQUIRED', 'REAUTH_REQUIRED'])

const CONFLICT_CODES = new Set([
  'CONFLICT',
  'OPTIMISTIC_LOCK_CONFLICT',
  'PRECONDITION_FAILED',
  'SUBSCRIPTION_ALREADY_CANCELED',
  'SUBSCRIPTION_ALREADY_PAUSED',
  'PLAN_CHANGE_NOOP',
  'REFUND_ALREADY_PROCESSED',
  'TRIAL_ALREADY_CONVERTED',
  'INVITE_ALREADY_USED',
  'IDEMPOTENCY_CONFLICT',
  'SCIM_CONFLICT',
  'USER_SESSION_ALREADY_REVOKED',
  'ALREADY_RESOLVED',
])

const DEGRADED_CODES = new Set([
  'IDENTITY_SERVICE_UNAVAILABLE',
  'BILLING_SERVICE_UNAVAILABLE',
  'CIRCUIT_BREAKER_OPEN',
  'BULKHEAD_FULL',
  'SECRETS_MANAGER_UNAVAILABLE',
  'TENANT_REALM_CREATION_FAILED',
  'TENANT_OWNER_ACCOUNT_CREATION_FAILED',
  'NETWORK_ERROR',
])

const RATE_LIMIT_CODES = new Set(['RATE_LIMIT_EXCEEDED', 'CAPTCHA_REQUIRED', 'CAPTCHA_INVALID'])

const PERMISSION_CODES = new Set([
  'FORBIDDEN',
  'POLICY_DENIED',
  'SUBSCRIPTION_ACCESS_DENIED',
  'API_KEY_SCOPE_DENIED',
  'REFUND_SELF_APPROVAL_FORBIDDEN',
  'SELF_APPROVAL_FORBIDDEN',
])

const PAYMENT_CODES = new Set([
  'PAYMENT_METHOD_REQUIRED',
  'BILLING_ADDRESS_REQUIRED',
  'TENANT_PAYMENT_FAILED',
  'INVOICE_PAYMENT_RETRY_FAILED',
  'PRORATION_NOT_AVAILABLE',
])

export function classifyError(error: unknown): ErrorClass {
  if (!isApiError(error)) return 'toast'
  const code = error.errorCode
  if (REAUTH_CODES.has(code)) return 'reauth'
  if (FIELD_CODES.has(code)) return 'field'
  if (BLOCKING_CODES.has(code)) return 'blocking'
  if (CONFLICT_CODES.has(code)) return 'conflict'
  if (RATE_LIMIT_CODES.has(code)) return 'rate-limit'
  if (PERMISSION_CODES.has(code)) return 'permission'
  if (PAYMENT_CODES.has(code)) return 'payment'
  if (DEGRADED_CODES.has(code) || error.status === 502 || error.status === 503) return 'degraded'
  if (code.startsWith('FRAUD_')) return 'toast' // generic copy — never echo fraud reasons
  return 'toast'
}

export function isReauthRequired(error: unknown): boolean {
  return isApiError(error) && REAUTH_CODES.has(error.errorCode)
}
